'use client';

import React from 'react';
import { Container } from '@/components/client/Container/Container';
import { Section } from '@/components/client/Section/Section';
import { ContactUsBanner } from '@/features/client/ContactUsBanner/ContactUsBanner';
import { ServiceCardsSection } from '@/features/client/ServiceCardsSection/ServiceCardsSection';
import { ContentLayout } from '@/features/client/Services/components/ContentLayout/ContentLayout';
import { RequestForm } from '@/features/client/Forms/RequestForm/RequestForm';
import { ServicesNavMenu } from '@/features/client/Services/components/ServicesNavMenu/ServicesNavMenu';
import { Content } from '@/features/client/Services/components/Content/Content';
import image from '@/public/images/services/main_page/remodeling.webp';
import { MainSectionLayout } from '@/features/client/MainSectionLayout/MainSectionLayout';
import { MainSection } from '@/features/client/MainSection/MainSection';

const data = {
  title: 'Remodeling Expertise',
  description: `At Tytum, we're dedicated to innovation and your home comfort. We aim to keep you comfortable year-round with energy-efficient and built to last solutions.\n\nWith extensive experience, we offer a comprehensive range of remodeling services to enhance both residential and commercial spaces. Our skilled team specializes in turning your remodeling ideas into reality with precision and craftsmanship.`,
  image: image,
  offer_title: 'Remodeling Service We Offer',
  offer_data: [
    {
      title: 'Kitchen Remodeling: ',
      list: [
        { title: '    Full Kitchen Renovation' },
        { title: 'Custom Kitchen Design' },
        { title: 'Cabinet Installation and Refacing' },
        { title: 'Countertop Installation (Granite, Quartz, Marble)' },
        { title: 'Kitchen Island Design and Installation' },
        { title: 'Backsplash Installation' },
        { title: 'Flooring Installation (Tile, Hardwood, Vinyl)' },
        { title: 'Appliance Installation and Integration' },
        { title: 'Lighting Design and Installation' },
        {
          title:
            '<a href="https://tytum.com/services/plumbing">Plumbing services</a> and electrical upgrades to ensure your kitchen remodel is as functional as it is beautiful.',
        },
      ],
    },
    {
      title: 'Bathroom Remodeling: ',
      list: [
        { title: '    Full Bathroom Renovation' },
        { title: 'Custom Bathroom Design' },
        { title: 'Shower and Bathtub Installation' },
        { title: 'Vanity and Sink Installation' },
        { title: 'Tile Installation (Walls, Floors, Showers)' },
        {
          title:
            'Bathroom Fixture Installation (Faucets, Toilets, Showerheads)',
        },
        { title: 'Walk-In Shower Conversions' },
        { title: 'Jacuzzi and Spa Bath Installation' },
        { title: 'Heated Flooring Installation' },
        { title: 'Lighting Design and Installation' },
      ],
    },
    {
      title: 'Whole-Home Remodeling:  ',
      list: [
        { title: '    Open Floor Plan Conversions' },
        { title: 'Room Additions and Extensions' },
        { title: 'Living Room and Dining Room Remodeling' },
        { title: 'Basement Finishing and Remodeling' },
        { title: 'Attic Conversions' },
        { title: 'Home Office Design and Build' },
        { title: 'Custom Built-Ins and Storage Solutions' },
        { title: 'Window and Door Replacement' },
        { title: 'Flooring Installation (Hardwood, Tile, Laminate)' },
        { title: 'Interior and Exterior Painting' },
      ],
    },

    {
      title: 'Outdoor Living Spaces:  ',
      list: [
        { title: '    Deck and Patio Design and Construction' },
        { title: 'Outdoor Kitchen Installation' },
        { title: 'Pergola and Gazebo Installation' },
        { title: 'Hardscaping and Landscaping Services' },
        { title: 'Outdoor Lighting Installation' },
        { title: 'Fire Pit and Outdoor Fireplace Installation' },
      ],
    },

    {
      title: 'Custom Carpentry and Millwork: ',
      list: [
        { title: '    Custom Cabinetry and Shelving' },
        { title: 'Crown Molding and Trim Installation' },
        { title: 'Wainscoting and Paneling' },
        { title: 'Custom Mantels and Built-In Units' },
        { title: 'Staircase Design and Installation' },
      ],
    },

    {
      title: 'Energy Efficiency Upgrades:  ',
      list: [
        { title: 'Insulation Installation and Upgrades' },
        { title: 'Window and Door Replacement for Energy Efficiency' },
        { title: 'Smart Home Integration' },
        { title: 'Energy-Efficient Lighting Solutions' },
        {
          title:
            'HVAC System Upgrades - including professional <a href="https://tytum.com/services/heating-and-air/air-conditioning">air conditioning service in San Diego</a> to keep your home comfortable and efficient year-round.',
        },
      ],
    },

    {
      title: 'Accessibility Remodeling:',
      list: [
        { title: '    ADA-Compliant Bathroom and Kitchen Upgrades' },
        { title: 'Wheelchair Ramps and Accessible Entrances' },
        { title: 'Walk-In Tubs and Roll-In Showers' },
        { title: 'Widening Doorways and Hallways' },
        { title: 'Lowered Countertops and Cabinets' },
      ],
    },
  ],
  banner_title: `“Transform Your Home with Tytum”`,
  banner_description:
    'Experience seamless remodeling with <a href="https://tytum.com/services/construction">Tytum construction</a>. From kitchens to bathrooms, we bring your vision to life with precision and care.',
  bookServiceButton: true,
  why_title: 'Why Choose Tytum for Remodeling Solutions?',

  why_description:
    'We know how crucial it is to promptly and effectively address problems to ensure the safety and functionality of your home. We deliver quality results on time and within budget, turning your dream home into a reality.',
  why_items: [
    {
      title: 'Comprehensive Remodeling Solutions:',
      description:
        'We handle every aspect of your remodeling project, from design to final touches.',
    },
    {
      title: 'Personalized Designs: ',
      description:
        'Our team works closely with you to create spaces that reflect your style and meet your needs.',
    },
    {
      title: 'Quality Craftsmanship:',
      description:
        'We pride ourselves on delivering high-quality workmanship with attention to detail.',
    },
    {
      title: 'Project Management:',
      description:
        'We manage every step of the remodeling process, ensuring projects are completed on time and within budget.',
    },
  ],
  why_footerText:
    'Trust us to be your go-to provider in the San Diego area and ensure the comfort and safety of your home. For clients looking to buy or sell property, we also partner with professionals offering <a href="https://tytum.com/services/brokerage">real estate brokerage services in San Diego</a>, helping you maximize the value of your remodeled home. Call us or fill out our online form for questions or a free quote.',
};

const RemodelingPage = () => {
  return (
    <div>
      <MainSectionLayout variant="primary" layout="snow">
        <MainSection
          label="Service Details"
          title="Remodeling"
          description="Transform your space with our expert remodeling services.
From kitchens to outdoor living areas, we ensure your home or business looks and functions exactly as you envision."
          withContactButton
        />
      </MainSectionLayout>

      <Section variant="secondary">
        <Container>
          <ContentLayout
            contentLeft={
              <>
                <ServicesNavMenu />
                <RequestForm />
              </>
            }
            contentRight={<Content data={data} />}
          />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ServiceCardsSection shortVariant={true} />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ContactUsBanner phone="+1(858) 289-4881 " />
        </Container>
      </Section>
    </div>
  );
};

export default RemodelingPage;
